import httpClient from "@/lib/api/http-client";
import type { IssuedAssetFile, UrlCaptureResult } from "@/lib/api/client";
import type {
  CancelQuestionnaireChatTurnResponse,
  CompileSurveyResponse,
  CoverageAssessment,
  EnqueueQuestionnaireChatTurnResponse,
  LaunchQuestionnaireResponse,
  QuestionnaireChatState,
  QuestionnaireChatTurnReadback,
  QuestionnaireDraftApiResponse,
  QuestionnaireOperationsRequest,
  QuestionnaireOperationsResultExtras,
  QuestionnairePreviewPassesResponse,
  QuestionnaireTurnCommand,
  SkipLogicDetection,
} from "@digital-twins/shared-types";


/** Time for the backend's own deadline to answer first, typed. */
const BACKEND_ANSWER_MARGIN_MS = 10_000;

/**
 * How long the builder waits for each kind of request before it counts as
 * `timeout`, a transport failure whose outcome is unknown. A timed-out write
 * is retried only with its own identity, never blindly: a turn by its
 * `clientTurnId`, a Save by its `expectedRevision` and replay rule, a launch
 * by its `idempotencyKey`. Each value is the backend's own bound for the
 * route (its clients' timeouts), plus a margin:
 * - `read`, `enqueue`, `write`: no model or page call on the request (a
 *   turn's stages run in its worker, not on the enqueue);
 * - `save`: per piece of media the operations route may capture a page
 *   (page-capture.client, 120 s), slice it (slice.client, 120 s) and read it
 *   (asset-analysis.client, 60 s). The route has no overall deadline, so a
 *   Save with several new pages can outlast this and reads as a timeout;
 * - `saveWithVideo`: a Save whose batch carries a video also watches it,
 *   beside the image reads (asset-analysis.client `readVideoAds`, 120 s);
 * - `compile` and `launch`: no model call; the compile and the bundle and
 *   launch writes, starting at the reviewed 120 s;
 * - `skipLogic`: skip-logic.client, 180 s;
 * - `coverage`: coverage.client, 120 s;
 * - `preview`: the compile (as `compile`), then preview-passes.client, 30 s;
 * - `upload`: no model call; the route stores the file, and the backend's
 *   HTTP server takes at most 300 s to receive a request (Node's default
 *   `requestTimeout`; the route sets none), so a longer upload already fails;
 * - `capture`: media-capture.controller's page capture, 150 s; storing the
 *   bounded capture it issues is an upload of a few MB inside the margin.
 */
export const BUILDER_REQUEST_TIMEOUT_MS = {
  read: 20_000,
  enqueue: 20_000,
  write: 30_000,
  save: 120_000 + 120_000 + 60_000 + BACKEND_ANSWER_MARGIN_MS,
  saveWithVideo: 120_000 + 120_000 + 120_000 + BACKEND_ANSWER_MARGIN_MS,
  compile: 120_000,
  launch: 120_000,
  skipLogic: 180_000 + BACKEND_ANSWER_MARGIN_MS,
  coverage: 120_000 + BACKEND_ANSWER_MARGIN_MS,
  preview: 120_000 + 30_000 + BACKEND_ANSWER_MARGIN_MS,
  upload: 300_000 + BACKEND_ANSWER_MARGIN_MS,
  capture: 150_000 + BACKEND_ANSWER_MARGIN_MS,
} as const;

const within = (kind: keyof typeof BUILDER_REQUEST_TIMEOUT_MS) => ({
  timeout: BUILDER_REQUEST_TIMEOUT_MS[kind],
});

/** Store a file the builder attaches to what the study tests. */
export async function uploadQuestionnaireAssetFileApi(
  file: File,
): Promise<IssuedAssetFile> {
  const formData = new FormData();
  formData.append("file", file);
  const response = await httpClient.post<IssuedAssetFile>(
    "/files/upload/questionnaire-concept-image",
    formData,
    within("upload"),
  );
  return response.data;
}

/**
 * Capture a page the builder was given. The backend stores the screenshot as
 * an asset file and returns it as `file`: the asset names that stored
 * capture, so the Save never captures the page again.
 */
export async function captureQuestionnaireUrlApi(
  url: string,
  opts?: { scale?: number; fullPage?: boolean },
): Promise<UrlCaptureResult> {
  const response = await httpClient.post<UrlCaptureResult>(
    "/media/capture-url",
    {
      url,
      issueAssetFile: true,
      ...(opts?.scale != null && { scale: opts.scale }),
      ...(opts?.fullPage != null && { fullPage: opts.fullPage }),
    },
    within("capture"),
  );
  return response.data;
}

export async function createQuestionnaireDraftApi(
  payload: Record<string, unknown>,
) {
  const response = await httpClient.post<QuestionnaireDraftApiResponse>(
    "/questionnaire-drafts",
    payload,
    within("write"),
  );
  return response.data;
}

export async function getQuestionnaireDraftApi(draftId: string) {
  const response = await httpClient.get<QuestionnaireDraftApiResponse>(
    `/questionnaire-drafts/${draftId}`,
    within("read"),
  );
  return response.data;
}

export async function archiveQuestionnaireDraftApi(draftId: string) {
  const response = await httpClient.delete(
    `/questionnaire-drafts/${draftId}`,
    within("write"),
  );
  return response.data;
}

export async function updateQuestionnaireDraftApi(
  draftId: string,
  payload: Record<string, unknown>,
) {
  const response = await httpClient.patch<QuestionnaireDraftApiResponse>(
    `/questionnaire-drafts/${draftId}`,
    payload,
    within("write"),
  );
  return response.data;
}

/**
 * One atomic, revision-bound write of typed edits. The 200 body is the draft
 * as `GET` returns it, compiled in the same commit, plus what the server
 * cascaded (`effects`).
 */
/** Whether a Save's batch gives an asset a video, which the Save watches. */
function batchCarriesVideo(body: QuestionnaireOperationsRequest): boolean {
  return body.operations.some((operation) =>
    (operation.op === "add_asset"
      ? operation.asset.media
      : operation.op === "set_asset_media"
        ? operation.media
        : []
    )?.some((media) => media.kind === "video"),
  );
}

export async function applyQuestionnaireOperationsApi(
  draftId: string,
  body: QuestionnaireOperationsRequest,
) {
  const response = await httpClient.post<
    QuestionnaireDraftApiResponse & QuestionnaireOperationsResultExtras
  >(
    `/questionnaire-drafts/${draftId}/operations`,
    body,
    within(batchCarriesVideo(body) ? "saveWithVideo" : "save"),
  );
  return response.data;
}

export async function compileQuestionnaireDraftApi(draftId: string, options: { validateOnly?: boolean } = {}) {
  const response = await httpClient.post<CompileSurveyResponse>(
    `/questionnaire-drafts/${draftId}/compile-survey`,
    options,
    within("compile"),
  );
  return response.data;
}

/**
 * The Preview's passes: the saved draft's validate-only compile, partitioned
 * by the engine (python-api `preview-passes`). `priorAnswers` are the shared
 * pass's answers, which seed the passes after it.
 */
export async function previewQuestionnairePassesApi(
  draftId: string,
  body: { priorAnswers?: Record<string, unknown> } = {},
) {
  const response = await httpClient.post<QuestionnairePreviewPassesResponse>(
    `/questionnaire-drafts/${draftId}/preview-passes`,
    body,
    within("preview"),
  );
  return response.data;
}

export async function launchQuestionnaireDraftApi(
  draftId: string,
  payload: Record<string, unknown>,
) {
  const response = await httpClient.post<LaunchQuestionnaireResponse>(
    `/questionnaire-drafts/${draftId}/launch`,
    payload,
    within("launch"),
  );
  return response.data;
}

export async function duplicateQuestionnaireDraftApi(
  draftId: string,
  payload: { title?: string } = {},
) {
  const response = await httpClient.post<{
    draftId: string;
    status: string;
    sourcePopulationId?: string;
  }>(`/questionnaire-drafts/${draftId}/duplicate`, payload, within("write"));
  return response.data;
}

/** Skip logic detected on the saved draft, each proposal judged; nothing is written. */
export async function inferSkipLogicApi(draftId: string) {
  const response = await httpClient.post<SkipLogicDetection>(
    `/questionnaire-drafts/${draftId}/infer-skip-logic`,
    undefined,
    within("skipLogic"),
  );
  return response.data;
}

/**
 * Coverage of an accepted revision. The server reads that revision's
 * questions, assets and audience itself, and refuses one that is no longer
 * current (409).
 */
export async function assessQuestionnaireCoverageApi(
  draftId: string,
  revision: number,
  signal?: AbortSignal,
) {
  const response = await httpClient.post<CoverageAssessment>(
    `/questionnaire-drafts/${draftId}/assess-coverage`,
    { revision },
    { ...within("coverage"), ...(signal ? { signal } : {}) },
  );
  return response.data;
}

export async function getQuestionnaireChatApi(draftId: string) {
  const response = await httpClient.get<QuestionnaireChatState>(
    `/questionnaire-drafts/${draftId}/chat`,
    within("read"),
  );
  return response.data;
}

/**
 * One chat turn (plan S4.2): a message or a typed command, never both. The
 * server reads the persisted draft; `clientTurnId` makes a retried enqueue
 * return the turn it already queued.
 */
export async function enqueueQuestionnaireChatTurnApi(
  draftId: string,
  input: {
    message?: string;
    command?: QuestionnaireTurnCommand;
    language?: string;
    clientTurnId: string;
    /** The draft revision the sender saw (S2c). */
    expectedRevision: number;
    /** The card option the message chose (plan S3.3, L16). */
    optionId?: string;
    /** The clarification record the turn answers, by id (plan S4.1). */
    answers?: string;
  },
  /** Ends the request: Stop before the turn is named, or the page leaving. */
  abortSignal?: AbortSignal,
) {
  const response = await httpClient.post<EnqueueQuestionnaireChatTurnResponse>(
    `/questionnaire-drafts/${draftId}/chat/turns`,
    {
      ...(input.command
        ? { command: input.command }
        : { message: input.message }),
      ...(input.language ? { language: input.language } : {}),
      clientTurnId: input.clientTurnId,
      expectedRevision: input.expectedRevision,
      ...(input.optionId ? { optionId: input.optionId } : {}),
      ...(input.answers ? { answers: input.answers } : {}),
    },
    { ...within("enqueue"), signal: abortSignal },
  );
  return response.data;
}

/** The draft's latest turn: what a reload re-attaches to. */
export async function getActiveQuestionnaireChatTurnApi(draftId: string) {
  const response = await httpClient.get(
    `/questionnaire-drafts/${draftId}/chat/turns/active`,
    within("read"),
  );
  return response.data;
}

/** One turn by id: what its waiter reads, never another turn. */
export async function getQuestionnaireChatTurnApi(
  draftId: string,
  turnId: string,
): Promise<QuestionnaireChatTurnReadback> {
  const response = await httpClient.get<QuestionnaireChatTurnReadback>(
    `/questionnaire-drafts/${draftId}/chat/turns/${turnId}`,
    within("read"),
  );
  return response.data;
}

export async function cancelQuestionnaireChatTurnApi(
  draftId: string,
  turnId: string,
) {
  // A request (plan S4.4): the turn's terminal event says how it ended.
  const response = await httpClient.post<CancelQuestionnaireChatTurnResponse>(
    `/questionnaire-drafts/${draftId}/chat/turns/${turnId}/cancel`,
    {},
    within("enqueue"),
  );
  return response.data;
}
